import type { HTMLAttributes } from 'react'
import type { SearchSummary } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import {Typography, VBox, VisibilityTrigger, LinearProgress, MenuList} from '@tunasong/ui-lib'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getAvatarOrIcon } from './avatar.js'
import { SearchResultListItem } from './search-result-item.js'

export interface SearchResultsProps<T extends SearchSummary | Persisted<Entity> = SearchSummary>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  entities: T[]
  similar?: T[]
  /** Is the search in progress? */
  searching?: boolean
  /** Default selected index */
  selectedIndex?: number
  /** Search input element */
  inputRef?: HTMLElement | null
  renderHotkeys?(entity: T, active: boolean): React.ReactElement | null
  onChange?(entity: T): void
  onHighlightChange?(entity: T): void
  /** Triggered when the user has scrolled to the end of the result set */
  onEndOfResults?(): Promise<void>
}

export function SearchResults<T extends SearchSummary>(props: SearchResultsProps<T>) {
  const {
    searching,
    inputRef,
    renderHotkeys,
    onChange,
    onHighlightChange,
    onEndOfResults,
    entities,
    similar,
    selectedIndex,
    ...boxProps
  } = props
  const plugins = usePlugins('all')

  const handleChange = useCallback(
    (entity: T) => () => {
      if (onChange) {
        onChange(entity)
      }
    },
    [onChange]
  )

  const [forcedActive, setActive] = useState<number>(-1)
  const active = typeof selectedIndex === 'number' ? selectedIndex : forcedActive

  /** Keep a ref in sync so the keydown handler always reads the latest value */
  const activeIndexRef = useRef(active)
  activeIndexRef.current = active

  /** Active MenuItem — used for scrollIntoView on keyboard navigation */
  const activeItemRef = useRef<HTMLElement | null>(null)

  /** Search input */

  const handleKey = useCallback(
    (ev: KeyboardEvent | React.KeyboardEvent) => {
      if (ev.key === 'ArrowDown') {
        ev.preventDefault()
        setActive(a => {
          const next = a < entities.length - 1 ? a + 1 : entities.length - 1
          activeIndexRef.current = next
          return next
        })
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault()
        setActive(a => {
          const next = a > 0 ? a - 1 : -1
          activeIndexRef.current = next
          return next
        })
      } else if (ev.key === 'Enter') {
        ev.preventDefault()
        const idx = activeIndexRef.current
        if (entities[idx] && onChange) {
          onChange(entities[idx])
        }
      }
    },
    [entities, onChange]
  )

  /** Register onKeyDown handlers */
  useEffect(() => {
    if (!inputRef) {
      return
    }
    inputRef.addEventListener('keydown', handleKey)
    return () => {
      inputRef.removeEventListener('keydown', handleKey)
    }
  }, [handleKey, inputRef])

  /** Scroll into view when active changes */
  useEffect(() => {
    const a = entities[active]
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'nearest' })
    }
    if (onHighlightChange) {
      onHighlightChange(a)
    }
  }, [activeItemRef, active, entities, onHighlightChange])

  const getItem = (entity: T, idx: number) => (
    <SearchResultListItem
      key={entity.id}
      ref={idx === active ? activeItemRef : undefined}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      icon={getAvatarOrIcon(entity, plugins!)}
      selected={idx === active}
      element={entity}
      breadCrumbLinks={false}
      onClick={handleChange(entity)}
      actions={renderHotkeys ? renderHotkeys(entity, idx === active) : null}
    />
  )

  if (!plugins) {
    return null
  }

  const hasResults = entities.length + (similar?.length ?? 0) > 0

  return (
    <VBox {...boxProps} style={{ flex: 1, ...boxProps?.style }}>
      {!hasResults ? (
        searching ? (
          <LinearProgress />
        ) : (
          <Typography style={{ padding: 16, color: 'var(--tuna-palette-action-disabled)' }}>No results</Typography>
        )
      ) : null}

      {hasResults ? (
        <>
          <MenuList onKeyDown={handleKey}>
            {entities.length > 0 ? entities.map((entity, idx) => getItem(entity, idx)) : null}
            {similar &&
              similar.length > 0 && [
                <Typography key={'similar-index'} style={{ padding: 16, color: 'var(--tuna-palette-action-disabled)' }}>
                  Similar to current doc
                </Typography>,
                ...similar.map((entity, idx) => getItem(entity, entities.length + idx)),
              ]}
          </MenuList>
          <VisibilityTrigger onVisible={onEndOfResults} />
        </>
      ) : null}
    </VBox>
  )
}
